import React from 'react';
import arraySupport from 'dayjs/plugin/arraySupport';
import isBetween from 'dayjs/plugin/isBetween';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import dayjs from '../../../dayjs/dayjs_timezone';
import Alert from '../../components/Alert';
import CloseModalIcon from '../components/CloseModalIcon';
import PaymentOptions from '../components/PaymentOptions';
import Price from '../components/Price';
import ProductWidgetPage from '../ProductWidgetPage';
import RentalDatePicker from '../../components/RentalDatePicker';
import Spinner from '../../components/Spinner';
import TimeSlotsSelect from '../components/TimeSlot/TimeSlotsSelect';
import { dateTimeFormat } from '../../utilities/timeUtility';
import {
  enumerateDaysBetweenDates,
  formatAvailabilityMessage,
  getMaxTimeslotsAvailability,
  getTooltipParams,
  isHiddenVariant,
  parseAvailableDates,
} from '../../utilities/helperUtility';
import Translation from '../../utilities/localizationUtility';
import { stringToBoolean } from '../../utilities/conversionUtility';
import { SHORT_DATE_FORMAT, DATE_TIME_FORMAT } from '../../constants';
import ErrorTooltip from '../components/ErrorTooltip';
import HelpTooltip from '../components/HelpTooltip';
import TooltipDetails from '../components/TooltipDetails';
import UpdatedBookingPage from '../components/UpdatedBookingPage';
import ConfirmationPage from '../components/RentalConfirmationPage';
import { getDaysArray, getTypeValue, getQuantity, isDateInDateRange } from './helpers';
import { StyledWrapperFields, StyledLabel, StyledInput } from './RentalPage.style';
import BrandingString from '../components/BrandingString';

dayjs.extend(arraySupport);
dayjs.extend(isBetween);

export default class RentalPage extends ProductWidgetPage {
  constructor(props) {
    super(props);

    this.state = {
      start: null,
      duration: 0,
      finish: null,
      finishError: false,
      enteredTo: null,
      durationValue: 0,
      hoverStart: null,
      daySelected: null,
      // Range select
      hoverEnteredTo: null,
      remainingCapacity: 0,
      availableDates: [],
      availableFinishDates: [],
      currentMonth: dayjs(Date.now()).startOf('month'),
      modalHeight: 100,
      isModalOpen: false,
      spaceOnDays: {},
      tooltipParams: null,
      messages: [],
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.onDayClickBound = this.onDayClick.bind(this);
    this.onMonthClickBound = this.onMonthClick.bind(this);
    this.handleDayMouseEnterBound = this.handleDayMouseEnter.bind(this);
    this.onSelectTimeSlotStart = this.onSelectTimeSlot.bind(this, true);
    this.onSelectTimeSlotFinish = this.onSelectTimeSlot.bind(this, false);
  }

  componentDidMount() {
    const {
      order: { variantSelected },
      orderActions: { setSelectedVariant },
      timeslots,
      variants,
    } = this.props;

    this.fetchDate();
    super.componentDidMount();
    this.setState({ availableDates: parseAvailableDates(timeslots, { force: this.showWaitlist() }) });

    if (!variantSelected && !!variants && !!variants[0]) {
      setSelectedVariant(variants[0].external_id);
    }
  }

  componentDidUpdate(oldProps, oldState) {
    const { finish } = this.state;
    if (!oldState.finish && finish) {
      this.setState({ finishError: false });
    }

    const {
      order: { quantity, selectedResource, selectedLocation, totalQuantity, variantSelected },
      orderActions: { setOrderDetails },
      variants,
      timeslots,
      setting,
    } = this.props;
    const { order: prevOrder, setting: prevSetting, timeslots: prevTimeslots } = oldProps;

    if (((selectedResource || {}).id !== (prevOrder.selectedResource || {}).id)
      || ((selectedLocation || {}).id !== (prevOrder.selectedLocation || {}).id)
      || (variantSelected !== prevOrder.variantSelected)
      || (prevSetting.show_time_picker !== setting.show_time_picker)
      || (prevSetting.slot_interval !== setting.slot_interval)
      || (prevSetting.time_slot_generation !== setting.time_slot_generation)
    ) {
      this.fetchDate();
    }

    if (!isEqual(timeslots, prevTimeslots)) {
      this.setState({
        availableDates: parseAvailableDates(timeslots, { force: this.showWaitlist() }),
        availableFinishDates: parseAvailableDates(timeslots, { key: 'finish_available_slot_count' }),
      });
    }

    if (
      !isEqual(timeslots, prevTimeslots)
      || !isEqual(quantity, prevOrder.quantity)
      || !isEqual(totalQuantity, prevOrder.totalQuantity)
    ) {
      const spaces = {};
      timeslots.forEach((slot) => { spaces[slot.date] = this.calculateSpaceOnDay(slot.date); });
      this.setState({ spaceOnDays: spaces });
    }

    if (!isEqual(variantSelected, prevOrder.variantSelected)) {
      const selectedVariant = variantSelected ? variants.find((v) => v.external_id === variantSelected) : {};
      const multiplier = selectedVariant ? selectedVariant.unit_multiplier : 1;
      setOrderDetails({ totalQuantity: quantity * multiplier });
    }
  }

  bookedDates() {
    const { timeslots } = this.props;

    if (!timeslots || this.showWaitlist()) {
      return [];
    }

    return timeslots.map((timeslot) => {
      const day = dayjs(timeslot.date);
      const isBooked = timeslot && timeslot.timeslots.every((slot) => slot.status === 3);
      return {
        from: isBooked && day.subtract(this.getOffset(null, false), 'days').toDate(),
        to: isBooked && day.toDate(),
      };
    });
  }

  calculateSpaceOnDay(day) {
    const { finishSelection, timeslots, order: { totalQuantity = 1 }, timeZone } = this.props;
    const dayFormat = dayjs.tz(day, timeZone).format(SHORT_DATE_FORMAT);
    const blockTimeSlot = find(timeslots, { date: dayFormat });

    if (blockTimeSlot) {
      return blockTimeSlot.timeslots.filter((x) => {
        const available = finishSelection || this.isRoom() ? x.finish_available : x.available;
        return available >= totalQuantity;
      }).length;
    }

    return 0;
  }

  isWeekend(day) {
    const dayOfWeek = day.day(); // 0 = Sunday, 1 = Monday, etc.
    return dayOfWeek === 0 || dayOfWeek === 6; // Saturday or Sunday
  }
  convertDateToUTC(date) {
    return date ? dayjs.tz(dayjs(date).format(DATE_TIME_FORMAT), this.props.timeZone).utc() : null;
  }

  fetchDate = () => {
    const { variants, order: { variantSelected } } = this.props;

    if (isHiddenVariant(variants, variantSelected)) {
      return;
    }

    let newState = {
      start: null,
      finish: null,
      enteredTo: null,
      durationValue: 0,
      duration: 0,
      remainingCapacity: 0,
      capacity: 0,
    };

    const {
      capacity,
      durations,
      options,
      timeZone,
      frameActions: { fetchBlocks, setFinishSelection },
      orderActions: { setNewVariantOption },
      order: { newVariantOption, selectedResource, selectedLocation },
    } = this.props;
    const { currentMonth, daySelected, enteredTo, finish, start } = this.state;
    const t = new Translation(this.props);
    const newSelectedVariant = variants.find((v) => v.external_id === variantSelected) || {};

    if (newVariantOption) {
      newState = {
        ...newState,
        ...{
          daySelected, start, finish, enteredTo,
        },
      };
    }

    if (!isEmpty(newSelectedVariant)) {
      // Set new capacity state
      newState.remainingCapacity = (getTypeValue(capacity.type) === 'variant')
        ? (newSelectedVariant.capacity || 0)
        : capacity.capacity;

      if (getTypeValue(capacity.type) === 'resource' && selectedResource) {
        newState.capacity = selectedResource.inventory;
      }

      // Set new duration state
      if (getTypeValue(durations.type) === 'variant') {
        const optionIdx = findIndex(options, (o) => o.name === durations.option);
        const optionDuration = durations.option_durations.find(
          (d) => d.value === newSelectedVariant.options[optionIdx],
        );

        if (this.isDurationOptionRange()) {
          const lowRange = optionDuration ? optionDuration.low_range : 86400;
          newState.durationValue = lowRange || 86400;
        } else {
          newState.durationValue = optionDuration ? optionDuration.duration : 0;
        }
      } else {
        newState.durationValue = durations.duration;
      }

      if (newState.durationValue !== undefined) {
        if (newState.durationValue && newState.durationValue < 86400) {
          newState.durationValue = 86400;
        }
        newState.duration = newState.durationValue / 86400;
      }

      let rangeStart = currentMonth.startOf('month').startOf('week').subtract(1, 'days');
      let rangeFinish = currentMonth.endOf('month').endOf('week').add(newState.duration + 1, 'days');
      const stateStart = newState.start && dayjs(newState.start);
      const stateFinish = newState.finish && dayjs(newState.finish);
      const interval = this.getInterval(newState);

      if (rangeStart.isSameOrAfter(stateStart)) {
        rangeStart = stateStart;
      }

      if (this.isRangeSelectionAllowed() && this.isDurationOptionRange() && stateFinish) {
        rangeFinish = currentMonth.endOf('month').endOf('week');
      }

      // The interval cannot be less than 5 minutes
      if (!!interval && interval < 5) {
        return;
      }

      // Fetch blocks
      fetchBlocks({
        externalId: variantSelected,
        interval,
        isConcat: false,
        finish: rangeFinish.format(SHORT_DATE_FORMAT),
        locationId: selectedLocation ? selectedLocation.id : null,
        resourceId: selectedResource ? selectedResource.id : null,
        start: rangeStart.format(SHORT_DATE_FORMAT),
        timezone: selectedLocation ? selectedLocation.timezone : timeZone,
        withFinishAvailability: this.showTimePicker(newState) || this.isRangeSelectionAllowed(newState),
      }).then(() => {
        if (!newVariantOption) return;
        this.setState({ finish: null, enteredTo: null }, () => {
          setNewVariantOption(null);
          this.onSetDate(finish);
          setFinishSelection(false);
        });
      });
    } else if (this.alert) {
      if (getTypeValue(capacity.type) === 'variant') {
        this.alert.show('red', [t.key('notAvailableVariantMessage')]);
      }
    }

    if (this.spinner) {
      this.spinner.show();
    }

    this.setState(newState);
  };

  findTimeInBlock(day, time) {
    if (!this.showTimePicker()) {
      return null;
    }

    const { availableBlocks, timeZone } = this.props;
    const block = availableBlocks.find((b) => dayjs(b.date).isSame(day, 'date'));

    if (!block) {
      return null;
    }

    const timeslot = block.timeslots.find(
      (slot) => dayjs(slot.start).isSame(this.convertDateToUTC(time)),
    ) || block.timeslots[0];

    if (!timeslot) {
      return null;
    }

    return dayjs(dayjs(timeslot.start).tz(timeZone).format(DATE_TIME_FORMAT));
  }

  getConditionForDisabled(value, strValue) {
    const { duration } = this.state;
    const currentDate = new Date(Date.now());
    const selectedDay = dayjs(value);

    return super.getConditionForDisabled(value, strValue)
        || dayjs(value).isBefore(currentDate.setHours(0, 0, 0, 0))
        || (duration === 0 && !this.isRoom())
        || (!this.showWaitlist() && this.getSpaceOnDay(strValue) === 0)
        || (this.isWeekend(selectedDay) && (this.props.defaultSetting.shop_count_days === 'weekdays')); // Disable weekends
  }

  getFinishForRangeSelection(start, finish) {
    if (!this.isRangeSelectionAllowed()) {
      return finish;
    }

    const { maxDuration, minDuration, rangeCountBasis } = this.props;
    const offset = rangeCountBasis === 0 ? 0 : 1;
    const maxDurationDate = start.add(maxDuration - offset, 'days');
    const minDurationDate = start.add(minDuration - offset, 'days');
    let modifiedFinish = finish;

    if (maxDuration && finish.isAfter(maxDurationDate)) {
      modifiedFinish = maxDurationDate;
    }
    if (minDuration && finish.isBefore(minDurationDate)) {
      modifiedFinish = minDurationDate;
    }
    if (rangeCountBasis === 0 && finish.isSame(start)) {
      modifiedFinish = finish.add(1, 'days');
    }

    return modifiedFinish;
  }

  getInterval(state = this.state) {
    if (!this.showTimePicker(state)) {
      return null;
    }

    const { setting } = this.props;
    if (setting.time_slot_generation === 'interval') {
      return setting.slot_interval || 60;
    }

    return (this.getDuration() || 0) / 60;
  }

  getNextButtonLabel(canBook) {
    const { start } = this.state;
    const t = new Translation(this.props);
    let label = canBook ? t.key('nextButton') : t.key('noAvailability');

    if (!start) {
      label = t.key('makeSelection');
    }

    return label;
  }

  getOffset(initialOffset, withLagTime = true) {
    const { lagTime, rangeCountBasis } = this.props;
    const { start, finish } = this.state;
    let offset = rangeCountBasis === 0 ? 1 : 0;

    if (initialOffset) {
      offset = initialOffset;
    }
    if (
      !this.selectedVariant().all_day
      || (rangeCountBasis === 0 && this.isRangeSelectionAllowed() && start && !finish)
    ) {
      offset = 0;
    }

    return withLagTime ? lagTime + offset : offset;
  }

  getSpaceOnDay(day) {
    const { spaceOnDays } = this.state;
    const { timeZone } = this.props;
    const dayFormat = dayjs.tz(day, timeZone).format(SHORT_DATE_FORMAT);
    const spaces = spaceOnDays[dayFormat];
    return spaces >= 0 ? spaces : this.calculateSpaceOnDay(day);
  }

  getSubmitButtonLabel() {
    const { bookingToken } = this.props;
    const { start } = this.state;
    const t = new Translation(this.props);
    let submitLabel;

    if (!start) {
      submitLabel = t.key('makeSelection');
    } else if (bookingToken) {
      submitLabel = t.key('reschedule') || 'Reschedule';
    }

    return submitLabel;
  }

  getValidMessage() {
    const { finish } = this.state;
    const t = new Translation(this.props);
    const messages = [];

    this.checkValidLocation(messages);
    this.checkValidResources(messages);

    if (!finish) {
      messages.push(t.key('missingFinishDateMessage'));
      this.setState({ finishError: true });
    }

    return messages;
  }

  handleAddItems(reservation, callback) {
    this.onAddItems(reservation, callback);
  }

  handleDayMouseEnter(day, e) {
    const { apiVerbose, availableBlocks, rangeCountBasis, timeslots } = this.props;
    const { start, duration } = this.state;
    const d = rangeCountBasis === 0 ? duration : duration - 1;
    const isStart = this.isStartSelection();
    const tooltipEnabled = apiVerbose && (!!window.bta.isDemo || !!window.xprops?.isShopifyLogin);
    let tooltipParams = null;
    // if hovered day is a booked block then return.

    if (tooltipEnabled) {
      tooltipParams = getTooltipParams(
        find(timeslots, { date: dayjs(day).format(SHORT_DATE_FORMAT) }),
        e.target,
      );
    }

    if (this.isBlockUnavailable(day) && tooltipParams) {
      this.setState({ tooltipParams });
      return;
    }

    const dayBlock = availableBlocks
      .map((block) => ({
        start: dayjs(block.date).toDate(),
        finish: this.getFinishForRangeSelection(dayjs(block.date), dayjs(block.date).add(d, 'days')).toDate(),
      }))
      .find((block) => dayjs(block.start).isSame(isStart ? day : start, 'date'));

    if (dayBlock) {
      this.setState({
        hoverStart: isStart ? dayBlock.start : start,
        hoverEnteredTo: isStart ? dayBlock.finish : this.selectHoverEntered(dayBlock.start, day),
      });
    } else if (tooltipParams) {
      this.setState({ tooltipParams });
    }
  }

  handleSubmit(e) {
    const { order, orderActions: { submitRentalOrder } } = this.props;
    this.setState({ messages: [] });
    const messages = this.getValidMessage();
    const items = [{ external_id: order.variantSelected, quantity: order.quantity }];

    this.onSubmit({
      element: e,
      messages,
      itemsResponse: items,
      action: submitRentalOrder,
    });
  }

  isBlockUnavailable(day) {
    const { timeslots } = this.props;

    if (!timeslots) {
      return false;
    }

    const timeslot = timeslots.find(
      (block) => (block.timeslots.every((slot) => slot.status === 2) && isDateInDateRange([{
        start: dayjs(block.date),
        finish: dayjs(block.date),
      }], day))
        || (block.timeslots.every((slot) => slot.status === 3) && isDateInDateRange([{
          start: dayjs(block.date).subtract(this.getOffset(null, false), 'days'),
          finish: dayjs(block.date),
        }], day)),
    );

    return !isEmpty(timeslot);
  }

  isDurationOptionRange() {
    const { durations, product } = this.props;
    return getTypeValue(durations.type) === 'variant' && product.duration_option_range_variant;
  }

  isOneDayVariant(state = this.state) {
    const { duration } = state;
    const selectedVariant = this.selectedVariant();
    return duration === 1 && selectedVariant.all_day;
  }

  isRangeSelectionAllowed(state = this.state) {
    const { setting } = this.props;
    return stringToBoolean(setting.allow_date_range) && (
      this.isDurationOptionRange()
      || this.isOneDayVariant(state)
      || this.showTimePicker(state)
      || this.isRoom()
    );
  }

  isRoom() {
    const { product: { profile } } = this.props;
    return profile === 'room';
  }

  isSlotAvailable(selectedDate) {
    if (!selectedDate) {
      return false;
    }

    const { order: { totalQuantity } } = this.props;
    const selectedTimeslots = this.selectedTimeslots(selectedDate);

    return selectedTimeslots.every((timeslot, index) => {
      let availableTimeslot = true;
      if (index === 0 || index === (selectedTimeslots.length - 1)) {
        availableTimeslot = this.startAvailable(timeslot);
      }
      const available = !this.isRangeSelectionAllowed() || index === 0 ? timeslot.available : timeslot.finish_available;
      return available >= totalQuantity && availableTimeslot;
    });
  }

  isStartSelection() {
    const { start, finish } = this.state;
    return !start || (start && finish);
  }

  isUnavailableBlock(date) {
    const { availableBlocks, finishSelection, timeslots, order: { totalQuantity } } = this.props;
    const day = dayjs(date);

    if (!timeslots.find((slot) => dayjs(slot.date).isSame(day, 'date'))) {
      return false;
    }

    const availableBlock = availableBlocks.find((block) => dayjs(block.date).isSame(day, 'date'));

    return isEmpty(availableBlock) || availableBlock.timeslots.every((timeslot, index) => {
      let unavailableTimeslot = false;

      if (index === 0 || index === (availableBlock.timeslots.length - 1)) {
        unavailableTimeslot = !this.startAvailable(timeslot);
      }

      const available = finishSelection ? timeslot.finish_available : timeslot.available;
      return available < totalQuantity || unavailableTimeslot;
    });
  }

  matchUnavailableBlocks(start, finish) {
    if (dayjs(start).isSame(finish, 'month')) {
      return getDaysArray(start, finish).some((date) => this.isUnavailableBlock(date));
    }

    return getDaysArray(dayjs(finish).startOf('month').toDate(), finish).some((date) => this.isUnavailableBlock(date));
  }

  maxQuantity() {
    const { start } = this.state;
    const { timeslots } = this.props;
    const selectedTimeslots = this.selectedTimeslots(start);
    const blackoutStatus = 2;

    if (selectedTimeslots.length) {
      return Math.min(...selectedTimeslots.filter((slot) => slot.status !== blackoutStatus).map((x) => x.available));
    }

    return getMaxTimeslotsAvailability(timeslots);
  }

  newVariantByOption(option) {
    if (!option) {
      return null;
    }
    const { durations, variants, options } = this.props;
    const selectedVariant = this.selectedVariant();
    const newOptions = [...selectedVariant.options];
    const optionIdx = findIndex(options, (o) => o.name === durations.option);
    newOptions[optionIdx] = option;

    return variants.find((v) => isEqual(v.options, newOptions));
  }

  onAddItems(reservation, callback) {
    const {
      additionalDetails,
      bookingFields,
      bookingToken,
      cartActions,
      lagTime,
      quantityRange,
      rangeCountBasis,
      setting,
      shopCountDays,
      timeZone,
    } = this.props;
    const reservationCopy = cloneDeep(reservation);

    if (reservationCopy) {
      reservationCopy.items = reservationCopy.items.slice(0, 1);
    }

    if (!reservationCopy || reservationCopy.items.some((x) => x.status !== 'ok')) {
      if (callback) {
        callback();
      }
    } else if (bookingToken) {
      this.onBookingReschedule(reservationCopy.items[0]);
    } else {
      const type = this.showTimePicker() ? ['_start_with_time'] : ['Start'];
      let quantity = null;
      const isRangeSelectionAllowed = this.isRangeSelectionAllowed();

      if (lagTime) {
        reservationCopy.items = reservationCopy.items.map((i) => ({
          ...i,
          finish: dayjs(i.finish).tz(timeZone).utc().subtract(lagTime * 1440, 'minutes')
            .format(),
        }));
      }

      if (stringToBoolean(setting.include_returned_date) || isRangeSelectionAllowed) {
        type.push(this.showTimePicker() ? '_finish_with_time' : 'Finish');
      }

      if (quantityRange && isRangeSelectionAllowed) {
        const { start, finish } = this.state;
        quantity = getQuantity(start, finish, rangeCountBasis, shopCountDays);
        type.push('Units');
      }

      type.push('Resource', 'Location');
      const fieldsSetting = { additionalDetails, bookingFields };

      cartActions.addItems(
        reservationCopy,
        type,
        fieldsSetting,
        {
          quantityRange: quantity,
          visibleResource: this.getEnableResourceDropdown(),
          isRental: true,
        },
      );
    }
  }

  // select day event.
  onDayClick(day) {
    if (this.isRangeSelectionAllowed()) {
      return this.onSetDate(day);
    }
    return this.onSetDateRange(day);
  }

  onMonthClick = (day) => {
    const { order: preOrder, exceededBookingsLimit, exceededTrialPlan, frameActions, timeZone } = this.props;
    const { duration, finish: finishDate, start: startDate } = this.state;
    const date = dayjs(day);
    const start = date.startOf('month').startOf('week').subtract(1, 'days');
    const finish = date.endOf('month').endOf('week').add(duration + 1, 'days');

    this.setState({ currentMonth: dayjs(day).startOf('month') });

    if (this.spinner) {
      this.spinner.show();
    }

    if (startDate && !finishDate && startDate < start.toDate()) {
      const match = this.matchUnavailableBlocks(startDate, dayjs(startDate).endOf('month').toDate());
      if (match) {
        this.setState({
          start: null,
          daySelected: null,
          finish: null,
          enteredTo: null,
        });
      }
    }

    const interval = this.getInterval();
    // The interval cannot be less than 5 minutes
    if (!!interval && interval < 5) {
      return;
    }

    if (!exceededBookingsLimit && !exceededTrialPlan) {
      frameActions.fetchBlocks({
        externalId: preOrder.variantSelected,
        interval,
        isConcat: true,
        finish: finish.format(SHORT_DATE_FORMAT),
        locationId: preOrder.selectedLocation ? preOrder.selectedLocation.id : [],
        resourceId: preOrder.selectedResource ? preOrder.selectedResource.id : [],
        start: start.format(SHORT_DATE_FORMAT),
        timezone: timeZone,
        withFinishAvailability: this.showTimePicker() || this.isRangeSelectionAllowed(),
      });
    }
  };

  onSelectTimeSlot(isStart, value) {
    const { orderActions: { setDateRange }, timeZone } = this.props;
    const { start, finish, duration } = this.state;
    const time = value ? dayjs(dayjs(value).tz(timeZone).format(DATE_TIME_FORMAT)).toDate() : null;
    const newStart = isStart ? time : start;
    const newFinish = isStart ? finish : time;
    const newState = isStart ? { start: newStart } : { finish: newFinish };

    setDateRange({
      start: this.convertDateToUTC(newStart)?.format(),
      finish: this.convertDateToUTC(newFinish)?.format(),
      duration,
    });
    this.setState(newState);
  }

  onSetDate(day) {
    const { rangeCountBasis, frameActions: { setFinishSelection }, product } = this.props;
    const { daySelected, start } = this.state;
    const selectedDay = dayjs(day).startOf('day');
    let selectedTime;

    if (this.isStartSelection() || dayjs(start).isAfter(dayjs(day), 'day')) {
      const offset = rangeCountBasis === 0 && !this.isRoom() ? 1 : 0;

      if (!this.showWaitlist() && this.matchUnavailableBlocks(day, dayjs(day).add(offset, 'days'))) {
        return;
      }
      selectedTime = this.findTimeInBlock(day, start) || selectedDay;
      if (this.isRoom()) {
        selectedTime = selectedTime.hour(product.check_in_time?.hour).minute(product.check_in_time?.minute);
      }

      this.setState({
        start: selectedTime.toDate(),
        daySelected: selectedDay.toDate(),
        finish: null,
        enteredTo: null,
      });
      setFinishSelection(true);
    } else {
      selectedTime = this.findTimeInBlock(day) || selectedDay;

      const selectDay = {
        start,
        daySelected,
        finish: selectedTime.toDate(),
        enteredTo: selectedDay.toDate(),
      };

      setFinishSelection(true);
      this.setDateRange(day, selectDay);
    }
  }

  onSetDateRange(day) {
    const { rangeCountBasis } = this.props;
    const { duration } = this.state;
    const selectedDuration = rangeCountBasis === 0 ? duration + 1 : duration;
    const daySelected = dayjs(day).startOf('day');
    const enteredTo = daySelected.add(selectedDuration, 'days').add(-1, 'seconds');

    const selectDay = {
      start: daySelected.toDate(),
      daySelected: daySelected.toDate(),
      finish: enteredTo.toDate(),
      enteredTo: enteredTo.toDate(),
    };

    this.setDateRange(day, selectDay);
  }

  selectedTimeslots(selectedDate) {
    const { timeslots } = this.props;
    const { start, finish } = this.state;
    const selectedTimeslots = [];

    for (let i = 0; i < (this.isRangeSelectionAllowed() ? getQuantity(start, finish, 1) : 1); i++) {
      const date = dayjs(selectedDate).add(i, 'day').format(SHORT_DATE_FORMAT);
      const blockTimeSlot = find(timeslots, { date });
      blockTimeSlot?.timeslots.forEach((slot) => {
        if (dayjs(slot.start).isSameOrAfter(this.convertDateToUTC(selectedDate))) {
          selectedTimeslots.push(slot);
        }
      });
    }

    return selectedTimeslots;
  }

  selectedVariant() {
    const { order: { variantSelected }, variants } = this.props;
    return variants.find((v) => v.external_id === variantSelected) || {};
  }

  selectHoverEntered(date, day) {
    const { product, rangeCountBasis } = this.props;
    const optionDuration = this.getOptionDuration(this.selectedVariant());

    if (!optionDuration || !product.duration_option_range_variant) {
      return this.getFinishForRangeSelection(dayjs(date), dayjs(day)).toDate();
    }
    const highRange = optionDuration.high_range;
    const offset = rangeCountBasis === 0 ? 86400 : 0;
    const max = dayjs(date).add(highRange + offset - 1, 'seconds');

    return dayjs(max).isSameOrAfter(dayjs(day)) ? day : max;
  }

  setDateRange(day, selectDay) {
    const {
      availableBlocks,
      blackouts,
      durations,
      order,
      orderActions: { setDateRange, setVariantsQuantity },
      frameActions: { setFinishSelection },
      rangeCountBasis,
      product,
    } = this.props;
    const { availableFinishDates, duration } = this.state;
    const showTimePicker = this.showTimePicker();
    const showWaitlist = this.showWaitlist();
    const dateFormat = showTimePicker ? DATE_TIME_FORMAT : SHORT_DATE_FORMAT;
    let modSelectDay = selectDay;
    modSelectDay = {
      ...modSelectDay,
      ...{
        finish: this.getFinishForRangeSelection(dayjs(modSelectDay.start), dayjs(modSelectDay.finish)).toDate(),
        enteredTo: this.getFinishForRangeSelection(dayjs(modSelectDay.start), dayjs(modSelectDay.finish)).toDate(),
      },
    };
    const { start, finish } = modSelectDay;
    let finishDate = dayjs(finish);

    if (!this.isStartSelection() && !showWaitlist) {
      if (this.isRoom()) {
        finishDate = finishDate.subtract(1, 'day');
      }
      if (this.matchUnavailableBlocks(start, finishDate) || !availableFinishDates.includes(finishDate.format('YYYY-MM-DD'))) {
        return;
      }
    }

    const availableBlock = availableBlocks.find((block) => dayjs(block.date).isSame(start, 'date'));
    const selectedRange = (showWaitlist || availableBlock) ? {
      start: dayjs(start).format(dateFormat),
      finish: dayjs(finish).format(dateFormat),
    } : {};

    // if selected day is a previous day of today then return.
    // || if selected day is a blackout block then return.
    // || if selected day is a booked block then return.
    if (
      dayjs(new Date(Date.now())).isAfter(new Date(day).setHours(0, 0, 0, 0), 'day')
      || (!showWaitlist && blackouts.length > 0 && blackouts.find((blackout) => dayjs(blackout.date).isSame(start, 'date')))
      || (!showWaitlist && this.isBlockUnavailable(day))
    ) {
      return;
    }

    if (this.isRangeSelectionAllowed() && this.isDurationOptionRange()) {
      const { option_durations } = durations;
      const defaultOptionDuration = this.getOptionDuration(this.selectedVariant());
      const offset = rangeCountBasis === 0 ? 86400 : 0;
      let newVariantOption = null;
      let matchMinMaxDuration = true;
      let optionDuration = option_durations.find((od) => {
        const highRange = od.high_range + offset;
        const lowRange = (od.low_range || 86400) + offset;
        return dayjs(finish).isSameOrAfter(dayjs(start).add(lowRange - 86400, 'seconds'))
          && dayjs(finish).isBefore(dayjs(start).add(highRange, 'seconds'));
      });

      if (!optionDuration) {
        matchMinMaxDuration = false;
        optionDuration = defaultOptionDuration;
      } else {
        newVariantOption = optionDuration.value;
      }

      const maxFinish = dayjs(start).add(optionDuration.high_range + offset - 1, 'seconds');

      setDateRange({
        start: selectedRange.start,
        finish: matchMinMaxDuration ? selectedRange.finish : maxFinish.format(dateFormat),
        duration,
      });

      if (!matchMinMaxDuration) {
        const newFinish = maxFinish.toDate();
        modSelectDay = {
          ...modSelectDay,
          ...{
            finish: newFinish,
            enteredTo: newFinish,
          },
        };
      }

      this.setState({ ...modSelectDay }, () => {
        const newVariant = this.newVariantByOption(newVariantOption);
        if (newVariant) {
          const { onTrigger } = this.props;
          setVariantsQuantity(`${newVariant.external_id}_quantity`, 1, {
            singleVariant: true,
            newVariantOption,
          });
          if (onTrigger) {
            onTrigger('variantChanged', newVariant);
          }
        }
        if (newVariant.external_id === order.variantSelected) {
           setFinishSelection(false);
        }
      });
    } else {
      const startDate = dayjs(selectedRange.start);
      let anotherFinishDate = this.getFinishForRangeSelection(startDate, dayjs(selectedRange.finish));

      if (this.isRoom()) {
        anotherFinishDate = anotherFinishDate.hour(product.check_out_time?.hour).minute(product.check_out_time?.minute);
      }

      setDateRange({
        start: startDate.format(dateFormat),
        finish: anotherFinishDate.format(dateFormat),
        duration,
      });

      modSelectDay = {
        ...modSelectDay,
        ...{
          finish: anotherFinishDate.toDate(),
          enteredTo: anotherFinishDate.toDate(),
        },
      };

      this.setState(modSelectDay);
      setFinishSelection(false);
    }
  }

  setDisabledDay(value) {
    let day = dayjs(value);
    day = day.format(SHORT_DATE_FORMAT);

    if (this.getConditionForDisabled(value, day)) {
      return new Date(day);
    }

    return null;
  }

  showTimePicker(state = this.state) {
    const { setting } = this.props;
    const { duration } = state;
    const selectedVariant = this.selectedVariant();

    return stringToBoolean(setting.show_time_picker) && !selectedVariant.all_day && duration <= 1 && !this.isRoom();
  }

  startAvailable(timeslot) {
    const { maxDate, minDate, timeZone } = this.props;
    const now = dayjs().tz(timeZone);
    const minTime = minDate ? now.add(minDate, 'seconds') : now;
    const maxTime = maxDate ? now.add(maxDate, 'days') : now.add(2, 'years');
    const start = dayjs(timeslot.start).tz(timeZone);

    return start.isSameOrAfter(minTime.startOf('day')) && start.isSameOrBefore(maxTime);
  }

  timeZone() {
    const { timeZone, order: { selectedLocation } } = this.props;
    return selectedLocation ? selectedLocation.timezone : timeZone;
  }

  renderTimeSlotsSelect(value, isStart) {
    const {
      order: { quantity },
      isFetchingBlocks,
      timeslots,
      timeZone,
      setting,
      maxDate,
      minDate,
      locale,
      style,
    } = this.props;
    const { start, availableDates } = this.state;
    const timeFormat = setting.time_format;
    const isShowRemainingSpace = stringToBoolean(setting.show_remaining_places);
    const block = cloneDeep(value ? find(timeslots, { date: dayjs(value).format(SHORT_DATE_FORMAT) }) : null);

    if (!isStart && block && block.timeslots[0]) {
      // correct finish timeslots
      const firstSlot = cloneDeep(block.timeslots[0]);
      block.timeslots = block.timeslots.filter(
        (slot) => dayjs(slot.start).tz(timeZone).isSame(dayjs(slot.finish).tz(timeZone), 'day'),
      );
      firstSlot.finish = firstSlot.start;
      block.timeslots.unshift(firstSlot);
    }

    const timeslot = block?.timeslots.find(
      (slot) => dayjs(isStart ? slot.start : slot.finish).isSame(dayjs(value)),
    );

    return (
      <TimeSlotsSelect
        isFinish={!isStart}
        isShowRemainingSpace={isShowRemainingSpace && isStart}
        timeslots={block ? [block] : []}
        timeZone={timeZone}
        loading={isFetchingBlocks}
        locale={locale}
        availableDates={availableDates}
        style={style}
        timeFormat={timeFormat}
        selectedTimeSlot={timeslot}
        selectedDate={value ? dayjs(value).format(SHORT_DATE_FORMAT) : null}
        onSelect={isStart ? this.onSelectTimeSlotStart : this.onSelectTimeSlotFinish}
        translation={new Translation(this.props)}
        quantity={quantity}
        maxDate={maxDate}
        minDate={minDate}
        timeRestriction={isStart ? null : this.convertDateToUTC(start)}
        variant={this.selectedVariant()}
      />
    );
  }

  render() {
    const { bookingUpdated } = this.state;
    if (bookingUpdated) {
      return <UpdatedBookingPage />;
    }

    const {
      blackouts,
      isFetchingBlocks,
      locale,
      locations,
      messages,
      order: { selectedLocation, selectedResource, variantSelected, quantity: selectedQuantity },
      product,
      productTitle,
      quantityRange,
      rangeCountBasis,
      region,
      setting,
      shopBeginningOfWeek,
      shopCountDays,
      shopDatepickerTimeInterval,
      shopDateFormat,
      style,
      timeslots,
      timeZone,
      variants,
      brandingVisualized,
    } = this.props;

    const {
      currentMonth,
      durationValue,
      enteredTo,
      finish,
      finishError,
      hoverEnteredTo,
      hoverStart,
      modalHeight,
      remainingCapacity,
      start,
      tooltipParams,
    } = this.state;
    const t = new Translation(this.props);
    const additionalStyle = { hoverColor: style.button.hover, textAlign: 'center', inactive: { ...style.inactive } };
    const daysBetween = enumerateDaysBetweenDates(hoverStart, hoverEnteredTo);
    const canBookTimes = this.showTimePicker() ? start && finish && start < finish : true;
    const canBook = !!variantSelected && (start || finish) && canBookTimes && this.isSlotAvailable(start);
    const selectedDateRange = {
      start: this.convertDateToUTC(start)?.format(),
      finish: this.convertDateToUTC(finish)?.format(),
    };
    const { errorText = {}, select = {} } = style;
    const isStart = this.isStartSelection();
    const isVisibleVariant = !isHiddenVariant(variants, variantSelected);
    const filteredBlackouts = blackouts.filter(
      (blackout) => (isStart ? blackout.available_slot_count : blackout.finish_available_slot_count) === 0,
    );
    const blackoutsArr = filteredBlackouts.map((blackout) => ({
      start: dayjs(blackout.date).toDate(),
      finish: dayjs(blackout.date).toDate(),
    }));
    let multiplier = 1;
    if (quantityRange && this.isRangeSelectionAllowed()) {
      multiplier = getQuantity(start, finish, rangeCountBasis, shopCountDays);
    }
    const selectedVariant = variants.find((v) => v.external_id === variantSelected);
    const price = selectedVariant && selectedVariant.price
      ? (selectedVariant.price * multiplier * selectedQuantity).toFixed(2)
      : Number(0).toFixed(2);
    const isShowRemainingSpace = stringToBoolean(setting.show_remaining_places);
    let startValue = start ? dayjs(start).format(dateTimeFormat(true)) : '';

    if (startValue && isShowRemainingSpace && !this.showTimePicker()) {
      const variant = this.selectedVariant();
      const block = cloneDeep(start ? find(timeslots, { date: dayjs(start).format(SHORT_DATE_FORMAT) }) : null);
      const timeslot = (block?.timeslots || [])[0];
      if (timeslot) {
        const unitMultiplier = variant?.unit_multiplier || 1;
        const available = parseInt(timeslot.available / unitMultiplier, 10);
        startValue += ` / ${formatAvailabilityMessage(available, available, t)} `;
      }
    }
    const isRenderWaitlistButton = this.showWaitlist() && start && finish && !canBook;

    return (
      <div className="center widget-container" style={{ minHeight: modalHeight + 20 }}>
        <CloseModalIcon />
        <Alert ref={this.setAlertRef} />
        <div className={`sub-panel rental-sub-panel ${setting.mode}`} style={{ ...style.body, ...style.boxShadow }}>
          {t.key('title').trim() && (
            <h3 className="text-center widget-title rental-widget-title" style={{ ...style.title }}>
              {t.key('title')}
            </h3>
          )}

          {this.renderExceededAlert(true)}
          {this.renderVariantSelect()}
          {this.renderQuantityInput(this.maxQuantity())}
          {isVisibleVariant && this.renderResourceDropdawn(false, true)}
          {isVisibleVariant && this.renderLocationDropdown()}

          <PaymentOptions />

          {isVisibleVariant && (
            <>
              <div className="relative">
                <Spinner ref={(ref) => (this.spinner = ref)} />
                <RentalDatePicker
                  to={finish}
                  from={start}
                  region={region}
                  setting={setting}
                  translation={t}
                  enteredTo={enteredTo}
                  hoverStart={hoverStart}
                  hoverEnteredTo={hoverEnteredTo}
                  onDayClick={this.onDayClickBound}
                  onMonthChange={this.onMonthClickBound}
                  handleDayMouseEnter={this.handleDayMouseEnterBound}
                  handleDayMouseLeave={this.handleDayMouseLeave}
                  isDisableCalendar={
                    // disable calendar while fetching blocks
                    isFetchingBlocks
                    // disable calendar when duration value equals to 0
                    || durationValue === 0
                    // disable calendar when remaining capacity equals to 0
                    // (when capacity types are variant_option and resource)
                    || remainingCapacity === 0
                  }
                  shopBeginningOfWeek={shopBeginningOfWeek}
                  styled={{ ...style.datepicker, ...additionalStyle }}
                  messages={messages}
                  month={currentMonth.toDate()}
                  modifiers={{
                    hoverStart,
                    hoverEnteredTo,
                    disabled: this.setDisabledDay,
                    hoverHighlight: daysBetween,
                    blackoutOverlap: blackoutsArr && !this.showWaitlist() ? blackoutsArr.map((blackoutEl) => ({
                      to: blackoutEl.finish,
                      from: blackoutEl.start,
                    })) : [],
                    bookedDates: this.bookedDates(),
                  }}
                  modifiersClassNames={{
                    hoverHighlight: 'rdp-day_hoverHighlight',
                    hoverStart: 'rdp-day_hoverStart',
                    hoverEnteredTo: 'rdp-day_hoverEnd',
                    disabled: 'rdp-day_disabled',
                    blackoutOverlap: 'rdp-day_blackoutOverlap',
                    bookedDates: 'rdp-day_bookedDates',
                  }}
                />
                <HelpTooltip
                  messageStyles={tooltipParams?.messageStyles}
                  onClose={() => this.setState({ tooltipParams: null })}
                  activateClassName="rdp-day"
                  show={!isEmpty(tooltipParams)}
                  hideIcon
                >
                  <TooltipDetails details={tooltipParams?.details} />
                </HelpTooltip>
              </div>

              <StyledWrapperFields>
                {finishError && <ErrorTooltip topOffset={-7} message={t.key('missingFinishDateMessage')} />}
                {!stringToBoolean(setting.hide_start_date) && (
                  <StyledLabel>
                    {t.key('pickUpDate')}
                    <StyledInput
                      type="text"
                      value={startValue}
                      borderColor={finishError ? errorText.color : select.color}
                      readOnly
                    />
                  </StyledLabel>
                )}
                {this.showTimePicker() && (
                  <StyledLabel>
                    {t.key('pickUpTime')}
                    {this.renderTimeSlotsSelect(start, true)}
                  </StyledLabel>
                )}

                {stringToBoolean(setting.include_returned_date) && (
                  <StyledLabel>
                    {t.key('returnDate')}
                    <StyledInput
                      type="text"
                      value={finish ? dayjs(finish).format(dateTimeFormat(true)) : ''}
                      borderColor={finishError ? errorText.color : select.color}
                      readOnly
                    />
                  </StyledLabel>
                )}
                {this.showTimePicker() && (
                  <StyledLabel>
                    {t.key('returnTime')}
                    {this.renderTimeSlotsSelect(finish)}
                  </StyledLabel>
                )}
              </StyledWrapperFields>
            </>
          )}

          <Price price={price} title={`${t.key('price')}`} />
          {this.renderWaitlist(start, { finish: finish })}

           {isRenderWaitlistButton
            ? (
              this.renderWaitlistButton()
            ) : (
              this.renderButton(canBook, isFetchingBlocks)
            )}

          {this.renderModal(
            <ConfirmationPage
              allDay={product.all_day}
              canBook={canBook}
              closeModal={this.closeModal ? this.closeModal : () => {}}
              dateFormat={shopDateFormat}
              enabledBookingFields={this.isEnabledBookingFields()}
              enableLocationDropdown={this.getEnableLocationDropdown()}
              enableResourceDropdown={this.getEnableResourceDropdown()}
              getValidationFn={this.handleGetValidationFn}
              locale={locale}
              locations={locations}
              price={price}
              productTitle={productTitle}
              renderSubmitButton={this.renderSubmitButton}
              selectedDateRange={selectedDateRange}
              selectedLocation={selectedLocation}
              selectedResource={selectedResource}
              selectedVariant={selectedVariant}
              setting={setting}
              setWrapperHeight={this.setWrapperHeight}
              style={style}
              timeInterval={shopDatepickerTimeInterval}
              timeZone={timeZone}
              translation={t}
            />,
            brandingVisualized
          )}
          {brandingVisualized && <BrandingString />}
        </div>
      </div>
    );
  }
}

RentalPage.propTypes = {};
